import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
// import './DeleteAccountModal.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  userEmail?: string;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userEmail
}) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmValid = confirmText === 'DELETE';

  const handleConfirm = async () => {
    if (!isConfirmValid || isDeleting) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      // Modal will be closed by parent component after successful deletion
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.deleteAccountError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    
    setConfirmText('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem 1.5rem 0 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            color: '#dc2626',
            fontSize: '1.25rem',
            fontWeight: 600
          }}>{t('profile.deleteAccount')}</h2>
          <button 
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onClick={handleClose}
            disabled={isDeleting}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h3 style={{
              margin: '0 0 0.5rem 0',
              color: '#dc2626',
              fontSize: '1.125rem',
              fontWeight: 600
            }}>{t('profile.deleteAccountWarning')}</h3>
            <p style={{
              margin: 0,
              color: '#6b7280',
              lineHeight: 1.5
            }}>{t('profile.deleteAccountDescription')}</p>
          </div>

          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <h4 style={{
              margin: '0 0 0.75rem 0',
              color: '#dc2626',
              fontSize: '1rem',
              fontWeight: 600
            }}>{t('profile.deleteAccountConsequences')}</h4>
            <ul style={{
              margin: 0,
              paddingLeft: '1.25rem',
              color: '#374151'
            }}>
              <li style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}>{t('profile.deleteAccountConsequence1')}</li>
              <li style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}>{t('profile.deleteAccountConsequence2')}</li>
              <li style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}>{t('profile.deleteAccountConsequence3')}</li>
              <li style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}>{t('profile.deleteAccountConsequence4')}</li>
            </ul>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{
              margin: '0 0 1rem 0',
              color: '#374151',
              fontWeight: 500
            }}>{t('profile.deleteAccountConfirmation', { email: userEmail })}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="confirm-text" style={{
                fontWeight: 500,
                color: '#374151',
                fontSize: '0.875rem'
              }}>
                {t('profile.deleteAccountTypeDelete')}
              </label>
              <input
                id="confirm-text"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={isDeleting}
                style={{
                  padding: '0.75rem',
                  border: `2px solid ${confirmText && !isConfirmValid ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  transition: 'border-color 0.2s',
                  backgroundColor: confirmText && !isConfirmValid ? '#fef2f2' : 'white',
                  ...(isDeleting && { backgroundColor: '#f9fafb', cursor: 'not-allowed' })
                }}
              />
            </div>
          </div>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '0.75rem',
              borderRadius: '6px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          justifyContent: 'flex-end'
        }}>
          <button
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontWeight: 500,
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              fontSize: '0.875rem',
              background: '#f3f4f6',
              color: '#374151',
              opacity: isDeleting ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
            onClick={handleClose}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </button>
          <button
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid #b91c1c',
              borderRadius: '8px',
              fontWeight: 500,
              cursor: (!isConfirmValid || isDeleting) ? 'not-allowed' : 'pointer',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              fontSize: '0.875rem',
              background: (!isConfirmValid || isDeleting) ? '#fca5a5' : '#dc2626',
              borderColor: (!isConfirmValid || isDeleting) ? '#fca5a5' : '#b91c1c',
              color: 'white',
              transition: 'all 0.2s'
            }}
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
          >
            {isDeleting ? t('profile.deletingAccount') : t('profile.deleteAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
